import React, { useCallback, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';

import { useGetRolesQuery, useGetSubscriptionsQuery } from 'api/main';
import {
    ORDER_BY,
    RoleNames,
    UserEnrichments,
    investorRoles,
    sortDirection,
} from 'utils/constants';
import { useMatchAtLeastOneRole } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import OngoingSubscriptionRow from './OngoingSubscriptionRow';
import { CustomTablePagination, EmptyTableBody, EnhancedTableHead } from 'components';
import { Table, TableBody, TableContainer, TableFooter, TableRow } from '@mui/material';

const tableHeads = [
    {
        id: ORDER_BY.lastNameAndCompany,
        width: 12,
        isSortable: true,
        label: texts.NOM,
        availableFor: ['transferred', 'untransferred'],
    },
    {
        id: ORDER_BY.fundName,
        width: 12,
        isSortable: true,
        label: texts.PRODUIT,
        availableFor: ['transferred', 'untransferred'],
    },
    {
        id: ORDER_BY.investmentSum,
        width: 12,
        isSortable: true,
        label: texts.MONTANT,
        availableFor: ['transferred', 'untransferred'],
    },
    {
        id: ORDER_BY.distributorFullName,
        width: 10,
        isSortable: true,
        label: texts.CONSULTANT,
        availableFor: ['transferred', 'untransferred'],
    },
    {
        id: 'phone',
        width: 9,
        isSortable: false,
        label: texts.TELEPHONE,
        availableFor: ['transferred'],
    },
    {
        id: ORDER_BY.email,
        width: 10,
        isSortable: true,
        label: texts.EMAIL,
        availableFor: ['transferred'],
    },
    {
        id: ORDER_BY.tunnelStep,
        width: 11,
        isSortable: false,
        label: texts.ETAPE_ACTUELLE,
        availableFor: ['transferred'],
    },
    {
        id: 'actions',
        width: 3,
        isSortable: false,
        label: '',
        availableFor: ['untransferred'],
    },
];

const defaultEnrichments = [
    UserEnrichments.INVESTOR_PROFILE,
    UserEnrichments.INVESTMENT_FUND,
    UserEnrichments.INVESTMENT_CHARACTERISTICS,
    UserEnrichments.ROLES,
    UserEnrichments.COMPANY,
    UserEnrichments.SUBSCRIPTIONS,
    UserEnrichments.DISTRIBUTOR_COMPANY,
    UserEnrichments.CREATED_BY,
];

const OngoingSubscriptionsTable = ({ type }) => {
    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const userIsAdmin = useMatchAtLeastOneRole([RoleNames.ADMIN_CIF]);

    const filteredColumns = useMemo(
        () =>
            tableHeads.filter(
                th =>
                    th.availableFor.includes(type) &&
                    (userIsAdmin || th.id !== ORDER_BY.distributorFullName)
            ),
        [type, userIsAdmin]
    );

    const [filters, setFilters] = useState({
        page: 1,
        orderBy: ORDER_BY.subscriptionId,
        direction: sortDirection.DESC,
        pendingTransfer: type === 'untransferred' ? 1 : undefined,
        transferred: type === 'transferred' ? 1 : undefined,
        enrichWith: defaultEnrichments,
        investmentFundEligible: 1,
    });

    const handleSortSubscriptions = useCallback(
        (property, direction) => {
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [setFilters]
    );

    const {
        data: { subscriptions: subscriptionList = [], lastPage: lastPageTable } = {
            subscriptions: [],
            lastPage: 1,
        },
        isFetching: listLoader,
    } = useGetSubscriptionsQuery(
        {
            role: investorRoles.map(role => role.id),
            itemsPerPage: 5,
            subscriptionStatus: ['in_progress'],
            ...filters,
        },
        { skip: !roles.length }
    );

    return (
        <TableContainer sx={{ '&:not(:last-child)': { mb: 8 } }}>
            <Table style={{ tableLayout: 'fixed' }}>
                <EnhancedTableHead
                    headCells={filteredColumns}
                    orderBy={filters.orderBy}
                    direction={filters.direction}
                    handleSort={handleSortSubscriptions}
                />
                {subscriptionList.length === 0 ? (
                    <EmptyTableBody
                        colSpan={filteredColumns.length}
                        loading={listLoader || rolesLoader}
                        message={texts.NO_RESULTS_FOUND}
                    />
                ) : (
                    <TableBody>
                        {subscriptionList.map(subscription => (
                            <OngoingSubscriptionRow
                                key={subscription.id}
                                type={type}
                                userIsAdmin={userIsAdmin}
                                subscription={subscription}
                            />
                        ))}
                    </TableBody>
                )}
                <TableFooter>
                    <TableRow>
                        <CustomTablePagination
                            loading={listLoader}
                            currentPage={filters.page}
                            lastPage={lastPageTable}
                            changePage={newPage => () =>
                                setFilters(prevState => ({ ...prevState, page: newPage }))
                            }
                            columns={filteredColumns.length}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
};

OngoingSubscriptionsTable.protpTypes = {
    type: PropTypes.oneOf(['untransferred', 'transferred']),
};

export default OngoingSubscriptionsTable;
